import { useCallback, useEffect, useState } from 'react'

import { createHighlight } from '@/api/api'
import { Highlight } from '@/api/services/highlight'

import { HighlightColor } from '../highlight/highlight-colors'
import { buildHighlightAreas, type HighlightArea } from '../pdf/highlight-areas'
import { useHighlightCrud } from './useHighlightCrud'

export type { HighlightArea }

export type PendingHighlight = {
  highlightAreas: HighlightArea[]
  selectedText: string
}

/** 文字を選択している間の情報（選択すると操作ボタンを出し、押されたらハイライト等に使う） */
export type PdfSelection = {
  selectedText: string
  highlightAreas: HighlightArea[]
  pageIndex: number
}

type UsePdfHighlightParams = {
  episodeId: number
  currentPage: number
}

type UsePdfHighlightReturn = {
  // 状態
  highlights: Highlight[]
  highlightSheetOpen: boolean
  highlightListOpen: boolean
  pendingHighlight: PendingHighlight | null
  editingHighlight: Highlight | null
  selection: PdfSelection | null

  // UI制御関数
  setHighlights: (highlights: Highlight[]) => void
  setHighlightSheetOpen: (open: boolean) => void
  setHighlightListOpen: (open: boolean) => void
  setPendingHighlight: (pending: PendingHighlight | null) => void
  setEditingHighlight: (editing: Highlight | null) => void
  clearSelection: () => void

  // ハイライト操作
  handleSaveHighlight: (color: HighlightColor, note: string) => Promise<void>
  handleUpdateHighlight: (id: number, color: HighlightColor, note: string) => Promise<void>
  handleDeleteHighlight: (id: number) => Promise<void>
  handleHighlightClick: (highlight: Highlight, e: React.MouseEvent) => void
  handleSelectionHighlight: () => void
}

export function usePdfHighlight({ episodeId, currentPage }: UsePdfHighlightParams): UsePdfHighlightReturn {
  // ハイライトCRUD共通hook
  const {
    highlights,
    setHighlights,
    handleUpdateHighlight,
    handleDeleteHighlight: crudDelete,
  } = useHighlightCrud({
    episodeId,
  })

  // ハイライトUI状態
  const [pendingHighlight, setPendingHighlight] = useState<PendingHighlight | null>(null)
  const [editingHighlight, setEditingHighlight] = useState<Highlight | null>(null)
  const [highlightSheetOpen, setHighlightSheetOpen] = useState(false)
  const [highlightListOpen, setHighlightListOpen] = useState(false)

  // 選択中の文字（PC・スマホとも、選択したらすぐシートを開かず操作ボタンを出す。EPUB と同じ動き）
  const [selection, setSelection] = useState<PdfSelection | null>(null)

  // Selection API でテキスト選択を監視
  useEffect(() => {
    const handleSelectionChange = () => {
      const current = window.getSelection()
      if (!current || current.isCollapsed || !current.rangeCount) {
        setSelection(null)
        return
      }

      const selectedText = current.toString().trim()
      if (!selectedText) {
        setSelection(null)
        return
      }

      try {
        // 選択範囲からハイライトエリアを事前に計算（ページをまたぐ選択にも対応）
        const highlightAreas = buildHighlightAreas(current.getRangeAt(0))
        if (highlightAreas.length === 0) {
          setSelection(null)
          return
        }
        setSelection({
          selectedText,
          highlightAreas,
          pageIndex: highlightAreas[0]?.pageIndex ?? currentPage,
        })
      } catch (error) {
        console.error('[PDF Selection] Error in selection change:', error)
        setSelection(null)
      }
    }

    document.addEventListener('selectionchange', handleSelectionChange)
    return () => {
      document.removeEventListener('selectionchange', handleSelectionChange)
    }
  }, [currentPage])

  /** 選択を解除して操作ボタンを消す */
  const clearSelection = useCallback(() => {
    window.getSelection()?.removeAllRanges()
    setSelection(null)
  }, [])

  // ハイライト保存処理
  const handleSaveHighlight = useCallback(
    async (color: HighlightColor, note: string) => {
      if (!pendingHighlight || !episodeId) return

      try {
        const newHighlight = await createHighlight({
          episode_id: episodeId,
          media_type: 'pdf',
          position_data: {
            highlightAreas: pendingHighlight.highlightAreas,
          },
          text: pendingHighlight.selectedText,
          color,
          note: note || undefined,
        })

        setHighlights((prev) => [...prev, newHighlight])
      } catch (error) {
        console.error('ハイライト保存に失敗しました:', error)
      } finally {
        setPendingHighlight(null)
        setHighlightSheetOpen(false)
      }
    },
    [pendingHighlight, episodeId, setHighlights],
  )

  // ハイライト更新処理（UI状態リセット付き）
  const handleUpdateWithReset = useCallback(
    async (id: number, color: HighlightColor, note: string) => {
      await handleUpdateHighlight(id, color, note)
      setEditingHighlight(null)
      setHighlightSheetOpen(false)
    },
    [handleUpdateHighlight],
  )

  // ハイライト削除処理（UI状態リセット付き）
  const handleDeleteWithReset = useCallback(
    async (id: number) => {
      await crudDelete(id)
      setEditingHighlight(null)
      setHighlightSheetOpen(false)
    },
    [crudDelete],
  )

  // ハイライトクリックハンドラー
  const handleHighlightClick = useCallback((highlight: Highlight, e: React.MouseEvent) => {
    e.stopPropagation()
    setEditingHighlight(highlight)
    setHighlightSheetOpen(true)
  }, [])

  // 選択中の文字をハイライトする（シートで色とメモを選ぶ）
  const handleSelectionHighlight = useCallback(() => {
    if (!selection) {
      return
    }

    // 既に計算済みのハイライトエリアを使用
    setPendingHighlight({
      highlightAreas: selection.highlightAreas,
      selectedText: selection.selectedText,
    })
    setHighlightSheetOpen(true)
    clearSelection()
  }, [selection, clearSelection])

  return {
    // 状態
    highlights,
    highlightSheetOpen,
    highlightListOpen,
    pendingHighlight,
    editingHighlight,
    selection,

    // UI制御関数
    setHighlights,
    setHighlightSheetOpen,
    setHighlightListOpen,
    setPendingHighlight,
    setEditingHighlight,
    clearSelection,

    // ハイライト操作
    handleSaveHighlight,
    handleUpdateHighlight: handleUpdateWithReset,
    handleDeleteHighlight: handleDeleteWithReset,
    handleHighlightClick,
    handleSelectionHighlight,
  }
}
